import { Trash } from '@phosphor-icons/react';
import { capitalize } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Confirmation } from 'core/components';
import { useAgGridTableContext } from 'core/states';
import { PricingProfilePricingType } from 'pricing/enums';
import { usePricingProfileActions } from 'pricing/hooks';
import { Color, Variant } from 'theme/enums';

interface IProps {
  type: PricingProfilePricingType;
}

export const RemoveSelectedProfiles = ({ type }: IProps) => {
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  const { t } = useTranslation();

  const { gridApi, selectedRows, deselectAllRows } = useAgGridTableContext();

  const { removePricingProfiles } = usePricingProfileActions({
    type,
    selectionCount: selectedRows?.length,
  });

  const capitalizedType = useMemo(() => capitalize(type), [type]);

  const onRemove = useCallback(async () => {
    setIsConfirmationOpen(false);

    const removedPricingProfilesIds = selectedRows?.map((profile) => profile.id);

    const { success } = await removePricingProfiles({
      ids: removedPricingProfilesIds,
    });

    if (!success) {
      return;
    }

    gridApi?.refreshServerSide();

    deselectAllRows();
  }, [deselectAllRows, gridApi, removePricingProfiles, selectedRows]);

  return (
    <>
      <Button
        text={t('buttonLabelRemove')}
        color={Color.DANGER}
        variant={Variant.SOFT}
        startDecorator={<Trash />}
        onClick={() => setIsConfirmationOpen(true)}
      />

      <Confirmation
        isOpen={isConfirmationOpen}
        title={t(`titleRemove${capitalizedType}Profiles`, {
          count: selectedRows?.length,
        })}
        message={t(`messageConfirmRemove${capitalizedType}Profiles`, {
          count: selectedRows?.length,
        })}
        confirmBtnLabel={t('buttonLabelRemove')}
        confirmBtnColor={Color.DANGER}
        onConfirm={onRemove}
        onCancel={() => setIsConfirmationOpen(false)}
      />
    </>
  );
};
